import { useEffect, useMemo, useRef } from "react";
import { useDocumentChatRuntime } from "@/store/documentChatRuntime";
import { ChatStatus, StreamPhase } from "@/constants/enums";

/**
 * Options for the useChatStreaming hook
 */
interface UseChatStreamingOptions {
  /** Whether streaming is currently active */
  isStreaming: boolean;
  /** The current pending question */
  pendingQuestion: string | null;
  /** Function to clear the pending question */
  clearPendingQuestion: () => void;
  /** Function to refetch messages from database */
  refetchMessages: () => Promise<any>;
  /** Scroll ref for managing scroll position */
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Callback to notify when post-stream refetch starts/ends */
  onRefetchStateChange?: (_isRefetching: boolean) => void;
}

/**
 * Return type for the useChatStreaming hook
 */
export interface UseChatStreamingReturn {
  /** Current streaming status */
  streamStatus: ChatStatus;
  /** Real-time streaming content */
  streamingContent: string;
  /** AI thinking process text */
  thinkingText: string;
  /** Active tool calls during response */
  toolCalls: any[];
  /** Current phase (thinking/answering/done) */
  phase: StreamPhase;
  /** Current processing node */
  currentNode: any;
  /** Whether all processing stages are complete */
  processingStagesComplete: boolean;
  /** Any streaming errors */
  error: string | null;
  /** Document sources from metadata */
  sourcesFromMetadata: any[];
  /** Thinking phase start time */
  thinkingStartAt: number | null;
  /** Answer phase start time */
  answerStartAt: number | null;
  /** Function to reset runtime state */
  resetRuntime: () => void;
  /** Calculated thinking duration in milliseconds */
  thinkingDurationMs: number;
}

/**
 * Custom hook for managing streaming chat state and effects.
 * Handles streaming state from runtime store and effects for cleanup/refetching.
 *
 * @param options - Configuration options for streaming behavior
 * @returns Streaming state and utilities
 *
 * @example
 * const {
 *   streamingContent,
 *   phase,
 *   error,
 *   thinkingDurationMs
 * } = useChatStreaming({
 *   isStreaming: true,
 *   pendingQuestion: "What is the timeline?",
 *   clearPendingQuestion: () => {},
 *   refetchMessages: async () => {},
 *   scrollRef: scrollRef
 * });
 */
export function useChatStreaming({
  isStreaming,
  pendingQuestion,
  clearPendingQuestion,
  refetchMessages,
  scrollRef,
  onRefetchStateChange,
}: UseChatStreamingOptions): UseChatStreamingReturn {
  // Real-time streaming state from document chat runtime store
  const streamStatus = useDocumentChatRuntime((s) => s.status);
  const streamingContent = useDocumentChatRuntime((s) => s.streamingContent);
  const thinkingText = useDocumentChatRuntime((s) => s.thinkingText);
  const toolCalls = useDocumentChatRuntime((s) => s.toolCalls);
  const phase = useDocumentChatRuntime((s) => s.phase);
  const currentNode = useDocumentChatRuntime((s) => s.currentNode);
  const processingStagesComplete = useDocumentChatRuntime((s) => s.processingStagesComplete);
  const error = useDocumentChatRuntime((s) => s.error);
  const sourcesFromMetadata = useDocumentChatRuntime((s) => s.sourcesFromMetadata);
  const thinkingStartAt = useDocumentChatRuntime((s) => s.thinkingStartAt);
  const answerStartAt = useDocumentChatRuntime((s) => s.answerStartAt);
  const resetRuntime = useDocumentChatRuntime((s) => s.resetRuntime);

  // Track if we're currently doing a post-stream refetch to prevent scroll jumps
  const isRefetchingRef = useRef(false);

  // Calculate thinking duration for display purposes
  const thinkingDurationMs = useMemo(() => {
    if (thinkingStartAt == null) return 0;
    const end = answerStartAt != null ? answerStartAt : Date.now();
    return Math.max(0, end - thinkingStartAt);
  }, [answerStartAt, thinkingStartAt]);

  // Effect: When streaming completes, refetch DB messages and clear pending state
  useEffect(() => {
    if (!isStreaming && pendingQuestion) {
      // If there's an error, keep the pending question and error state visible
      // The user needs to either retry or manually dismiss by leaving the chat
      if (error) {
        return; // Don't clear anything - let the error state persist
      }

      const timeoutId = setTimeout(async () => {
        const el = scrollRef.current;

        // Capture current scroll position before any DOM changes
        const savedScrollTop = el?.scrollTop ?? 0;
        const savedScrollHeight = el?.scrollHeight ?? 0;

        // If we produced content, refetch DB messages to get the saved answer
        if (streamingContent && streamingContent.length > 0) {
          // Mark that we're refetching to prevent scroll jumps
          isRefetchingRef.current = true;
          onRefetchStateChange?.(true);

          try {
            await refetchMessages();
          } finally {
            // Clear refetch flag - scroll restoration happens after clearing pending question
            isRefetchingRef.current = false;
            onRefetchStateChange?.(false);
          }
        }

        // Clear pending state only when there's no error
        // This happens after refetch to ensure smooth transition
        clearPendingQuestion();

        // Restore scroll position after all DOM updates (refetch + pending question clear)
        // Use double RAF to ensure all React updates have been applied
        if (el) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (el) {
                // Calculate the difference in scroll height
                const newScrollHeight = el.scrollHeight;
                const heightDiff = newScrollHeight - savedScrollHeight;

                // Restore scroll position accounting for new content and layout changes
                el.scrollTop = savedScrollTop + heightDiff;
              }
            });
          });
        }

        // Always reset runtime at the end of streaming to avoid stale UI state
        resetRuntime();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    isStreaming,
    pendingQuestion,
    streamingContent,
    refetchMessages,
    resetRuntime,
    error,
    clearPendingQuestion,
    scrollRef,
    onRefetchStateChange,
  ]);

  return {
    streamStatus,
    streamingContent,
    thinkingText,
    toolCalls,
    phase,
    currentNode,
    processingStagesComplete,
    error,
    sourcesFromMetadata: sourcesFromMetadata ?? [],
    thinkingStartAt,
    answerStartAt,
    resetRuntime,
    thinkingDurationMs,
  };
}
